// src/api/areasCocina.js
import { apiFetch } from './index.js';
import { toTitleCase } from '@/utils/stringUtils.js';

const convertirDesdeAPI = (apiArea) => {
  if (!apiArea) return null;
  let source = apiArea;
  if (apiArea.data && !Array.isArray(apiArea.data) && typeof apiArea.data === 'object') source = apiArea.data;
  else if (apiArea.area && typeof apiArea.area === 'object') source = apiArea.area;

  const getProp = (obj, propName) => {
    if (!obj) return undefined;
    const keys = Object.keys(obj);
    const normalizedProp = propName.toLowerCase().replace(/_/g, '');
    const foundKey = keys.find(k => k.toLowerCase().replace(/_/g, '') === normalizedProp);
    return foundKey ? obj[foundKey] : undefined;
  };

  const id = source.idAreaCocina || source.IdAreaCocina || getProp(source, 'idAreaCocina') || source.id;

  return {
    idAreaCocina: id, id,
    nombre: toTitleCase(source.nombre || source.Nombre || ''),
    descripcion: source.descripcion || source.Descripcion || '',
    impresora: source.impresora || source.Impresora || '',
    estado: (source.estado || source.Estado || 'activo').toLowerCase(),
    orden: source.orden ?? source.Orden ?? 0,
    fecha_creacion: source.fecha_creacion || source.fechaCreacion || source.FechaCreacion || null,
    // Relaciones
    TotalProductos: source.TotalProductos ?? source.totalProductos ?? 0,
    TotalComandas: source.TotalComandas ?? source.totalComandas ?? 0,
    ComandasPendientes: source.ComandasPendientes ?? source.comandasPendientes ?? 0,
    ...source, idAreaCocina: id,
  };
};

const convertirParaAPI = (frontendArea) => ({
  nombre: toTitleCase((frontendArea.nombre || '').trim()),
  descripcion: frontendArea.descripcion || null,
  impresora: frontendArea.impresora || null,
  estado: frontendArea.estado || 'activo',
  orden: Number(frontendArea.orden) || 0,
});

const convertirListaDesdeAPI = (apiList) => {
  let lista = apiList;
  if (apiList && apiList.data && Array.isArray(apiList.data)) lista = apiList.data;
  else if (apiList && apiList.areas && Array.isArray(apiList.areas)) lista = apiList.areas;
  if (!Array.isArray(lista)) return [];
  return lista.map(convertirDesdeAPI);
};

export const formatearFechaArea = (fechaISO, completo = false) => {
  if (!fechaISO) return 'N/A';
  try {
    const fecha = new Date(fechaISO);
    const opciones = { year: 'numeric', month: 'short', day: 'numeric' };
    if (completo) { opciones.hour = '2-digit'; opciones.minute = '2-digit'; }
    return fecha.toLocaleDateString('es-ES', opciones);
  } catch { return fechaISO; }
};

export const convertirEstadoATexto = (estado) => {
  if (!estado) return 'Sin estado';
  return estado.charAt(0).toUpperCase() + estado.slice(1);
};

export const convertirEstadoABoolean = (estado) => estado?.toLowerCase() === 'activo';

export const obtenerEstadisticasAreas = (areas) => {
  const activas = areas.filter(a => a.estado === 'activo').length;
  const inactivas = areas.filter(a => a.estado === 'inactivo').length;
  const conImpresora = areas.filter(a => a.impresora && a.impresora.trim() !== '').length;
  return {
    total: areas.length,
    activas, inactivas, conImpresora,
    porcentajeActivas: areas.length > 0 ? Math.round((activas / areas.length) * 100) : 0,
  };
};

export const buscarAreas = (areas, termino) => {
  if (!termino || termino.trim() === '') return areas;
  const t = termino.toLowerCase();
  return areas.filter(a =>
    a.nombre?.toLowerCase().includes(t) ||
    a.descripcion?.toLowerCase().includes(t) ||
    a.impresora?.toLowerCase().includes(t)
  );
};

export const filtrarAreasPorEstado = (areas, estado) => {
  if (estado === 'all') return areas;
  if (estado === 'activos') return areas.filter(a => a.estado === 'activo');
  if (estado === 'inactivos') return areas.filter(a => a.estado === 'inactivo');
  return areas;
};

// ================= API calls =================

export const obtenerAreas = async () => {
  try {
    const response = await apiFetch('/AreaCocina');
    const areas = convertirListaDesdeAPI(response);
    return { success: true, data: areas, count: areas.length };
  } catch (error) {
    return { success: false, error: error.message, data: [], count: 0 };
  }
};

export const obtenerAreasActivas = async () => {
  try {
    const response = await apiFetch('/AreaCocina/activas');
    const areas = convertirListaDesdeAPI(response);
    return { success: true, data: areas, count: areas.length };
  } catch (error) {
    return { success: false, error: error.message, data: [], count: 0 };
  }
};

export const obtenerAreaPorId = async (id) => {
  try {
    if (!id) throw new Error('ID de área requerido');
    const response = await apiFetch(`/AreaCocina/${id}`);
    return { success: true, data: convertirDesdeAPI(response) };
  } catch (error) {
    return { success: false, error: error.message, data: null };
  }
};

export const obtenerAreaConRelaciones = async (id) => {
  try {
    if (!id) throw new Error('ID de área requerido');
    const response = await apiFetch(`/AreaCocina/${id}/conRelaciones`);
    return { success: true, data: convertirDesdeAPI(response) };
  } catch (error) {
    return { success: false, error: error.message, data: null };
  }
};

export const buscarAreasPorCriterios = async (criterios = {}) => {
  try {
    const response = await apiFetch('/AreaCocina/search', {
      method: 'POST',
      body: JSON.stringify({
        nombre: criterios.nombre || null,
        estado: criterios.estado || null,
      }),
    });
    return { success: true, data: convertirListaDesdeAPI(response) };
  } catch (error) {
    return { success: false, error: error.message, data: [] };
  }
};

export const verificarAreaPorId = async (id) => {
  try {
    const response = await apiFetch(`/AreaCocina/${id}/exists`);
    return { success: true, existe: response.existe ?? response.exists ?? false };
  } catch (error) {
    return { success: false, error: error.message, existe: false };
  }
};

export const crearArea = async (areaData) => {
  try {
    if (!areaData.nombre || areaData.nombre.trim() === '') throw new Error('El nombre es requerido');
    const response = await apiFetch('/AreaCocina', {
      method: 'POST',
      body: JSON.stringify(convertirParaAPI(areaData)),
    });
    return { success: true, data: convertirDesdeAPI(response), message: 'Área creada' };
  } catch (error) {
    return { success: false, error: error.message, data: null };
  }
};

export const actualizarArea = async (id, areaData) => {
  try {
    if (!id) throw new Error('ID requerido');
    if (!areaData.nombre || areaData.nombre.trim() === '') throw new Error('El nombre es requerido');
    const response = await apiFetch(`/AreaCocina/${id}`, {
      method: 'PUT',
      body: JSON.stringify(convertirParaAPI(areaData)),
    });
    return { success: true, data: convertirDesdeAPI(response), message: 'Área actualizada' };
  } catch (error) {
    return { success: false, error: error.message, data: null };
  }
};

export const eliminarArea = async (id) => {
  try {
    if (!id) throw new Error('ID requerido');
    const response = await apiFetch(`/AreaCocina/${id}`, { method: 'DELETE' });
    return { success: true, data: response, message: response?.mensaje || 'Área desactivada' };
  } catch (error) {
    let errorMsg = 'Error al desactivar el área';
    if (error.response?.data?.mensaje) errorMsg = error.response.data.mensaje;
    else if (error.response?.data?.error) errorMsg = error.response.data.error;
    else if (error.message) errorMsg = error.message;
    return { success: false, error: errorMsg, data: null };
  }
};

export const activarArea = async (id) => {
  try {
    const areaResult = await obtenerAreaPorId(id);
    if (!areaResult.success || !areaResult.data) throw new Error('No se pudo obtener el área');
    const area = areaResult.data;
    area.estado = 'activo';
    return await actualizarArea(id, area);
  } catch (error) {
    return { success: false, error: error.message, data: null };
  }
};

export const desactivarArea = async (id) => {
  try {
    const areaResult = await obtenerAreaPorId(id);
    if (!areaResult.success || !areaResult.data) throw new Error('No se pudo obtener el área');
    const area = areaResult.data;
    area.estado = 'inactivo';
    return await actualizarArea(id, area);
  } catch (error) {
    return { success: false, error: error.message, data: null };
  }
};

export default {
  obtenerAreas, obtenerAreasActivas, obtenerAreaPorId, obtenerAreaConRelaciones,
  buscarAreasPorCriterios, verificarAreaPorId,
  crearArea, actualizarArea, eliminarArea, activarArea, desactivarArea,
  formatearFechaArea, convertirEstadoATexto, convertirEstadoABoolean,
  obtenerEstadisticasAreas, buscarAreas, filtrarAreasPorEstado,
  convertirDesdeAPI, convertirParaAPI,
};